import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function PlacedCandidatesList() {
  const [placements, setPlacements] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [placementToDelete, setPlacementToDelete] = useState(null);

  const location = useLocation(); // 👈 used to refresh after navigation

  // Fetch data when component loads OR when coming back from update page
  useEffect(() => {
    fetchPlacements();
  }, [location]);

  const fetchPlacements = async () => {
    try {
      const result = await axios.get(
        `${process.env.REACT_APP_API_URL}placements/fetchallplacedcandidates`
      );

      const updatedData = result.data.map((item) => {
        let imageUrl = "";

        if (item.image) {
          imageUrl = `${process.env.REACT_APP_IMAGE_URL}${item.image}`;
        }

        return {
          ...item,
          image: imageUrl,
        };
      });

      setPlacements(updatedData);
      setFilteredList(updatedData);
    } catch (error) {
      console.log("Error fetching placements:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search filter
  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredList(placements);
    } else {
      setFilteredList(
        placements.filter(
          (item) =>
            (item.full_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.company_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (item.designation || "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [searchTerm, placements]);

  const handleOpenDeleteModal = (placement) => {
    setPlacementToDelete(placement);
    setShowDeleteModal(true);
  };

  // DELETE FUNCTION
  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}placements/deleteplacedcandidate/${placementToDelete.placement_id}`
      );

      // Remove from UI instantly
      setPlacements((prev) =>
        prev.filter((item) => item.placement_id !== placementToDelete.placement_id)
      );

      toast.success("Placement deleted successfully", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
      setShowDeleteModal(false);
    } catch (error) {
      console.log("Delete error:", error);
      toast.error("Failed to delete placement", {
        position: "top-right",
        autoClose: 2000,
        theme: "colored",
      });
      setShowDeleteModal(false);
    }
  };

  return (
    <div>
      <ToastContainer />

      {/* Main Card Container */}
      <div className="main-card">

        {/* Toolbar Section */}
        <div className="toolbar-container">
          <h2 className="page-title">Placements List</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="results-count">{filteredList.length} results</span>
            <input
              type="text"
              className="search-box"
              placeholder="Search by Name / Company / Designation"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Link to="/createPlacement" className="btn-add-primary">
              + Add New Placement
            </Link>
          </div>
        </div>

        {/* Gradient Header */}
        <div className="table-header-panel">
          Placements List
        </div>

        {/* Table wrapper with overflowX */}
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th style={{ padding: '10px 8px' }}>SR No.</th>
                <th style={{ padding: '10px 8px' }}>Image</th>
                <th style={{ padding: '10px 8px' }}>Employee ID</th>
                <th style={{ padding: '10px 8px' }}>Full Name</th>
                <th style={{ padding: '10px 8px' }}>Designation</th>
                <th style={{ padding: '10px 8px' }}>Company</th>
                <th style={{ padding: '10px 8px' }}>Year</th>
                <th style={{ padding: '10px 8px' }}>Location</th>
                <th style={{ padding: '10px 8px' }}>Package (LPA)</th>
                <th style={{ padding: '10px 8px' }}>Status</th>
                <th style={{ padding: '10px 8px' }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11">
                    <div className="loading-container">
                      <div className="spinner"></div>
                    </div>
                  </td>
                </tr>
              ) : filteredList.length === 0 ? (
                <tr>
                  <td colSpan="11">
                    <div className="empty-state">
                      <div className="empty-state-icon">👤</div>
                      <div className="empty-state-text">No Placements Found</div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredList.map((placement, index) => (
                  <tr key={placement.placement_id}>
                    <td style={{ padding: '8px' }}>{index + 1}</td>
                    <td style={{ padding: '8px' }}>
                      {placement.image ? (
                        <img
                          src={placement.image}
                          alt={placement.full_name}
                          loading="lazy"
                          style={{
                            width: "50px",
                            height: "50px",
                            objectFit: "cover",
                            borderRadius: "6px",
                          }}
                        />
                      ) : (
                        "No Image"
                      )}
                    </td>
                    <td style={{ padding: '8px' }}>{placement.employee_id}</td>
                    <td style={{ padding: '8px', fontWeight: '600' }}>{placement.full_name}</td>
                    <td style={{ padding: '8px' }}>{placement.designation}</td>
                    <td style={{ padding: '8px' }}>{placement.company_name}</td>
                    <td style={{ padding: '8px' }}>{placement.placement_year}</td>
                    <td style={{ padding: '8px' }}>{placement.location}</td>
                    <td style={{ padding: '8px' }}>{placement.package_lpa ? parseFloat(placement.package_lpa).toFixed(2) : "0.00"}</td>

                    <td style={{ padding: '8px' }}>
                      {placement.isActive == 1 ? (
                        <span className="badge-active">Active</span>
                      ) : (
                        <span className="badge-inactive">Inactive</span>
                      )}
                    </td>

                    <td style={{ padding: '8px' }}>
                      <div className="action-btn-container">
                        <Link
                          to={`/placementUpdate/${placement.placement_id}`}
                          className="btn-action-edit"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleOpenDeleteModal(placement)}
                          className="btn-action-delete"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Placement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete placement for "{placementToDelete?.full_name}"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
